import { useCallback, useEffect, useState } from "react";
import { useLatestRequest } from "../../../hooks/useLatestRequest";
import { getApiErrorMessage } from "../../../lib/result-codes";
import { getHeatMapCategoryOptions, getHeatMapNeighborhoodOptions } from "../api/heat-map.api";
import type { OptionModel } from "../types/Filter";

export const useDashboardOptions = (organizationId?: string) => {
  const [neighborhoods, setNeighborhoods] = useState<OptionModel[]>([]);
  const [categories, setCategories] = useState<OptionModel[]>([]);
  const [error, setError] = useState<string | null>(null);
  const { run: runLatestRequest, loading } = useLatestRequest();

  const fetchData = useCallback(async () => {
    if (!organizationId) return undefined;

    try {
      setError(null);
      const result = await runLatestRequest(async () => {
        const [neighborhoodOptions, categoryOptions] = await Promise.all([
          getHeatMapNeighborhoodOptions(organizationId ?? ""),
          getHeatMapCategoryOptions(),
        ]);
        return { neighborhoodOptions, categoryOptions };
      });

      if (!result) return undefined;

      setNeighborhoods(result.neighborhoodOptions);
      setCategories(result.categoryOptions);
      return result;
    } catch (fetchError) {
      setError(getApiErrorMessage(fetchError, "Failed to load filter options"));
      return undefined;
    }
  }, [organizationId, runLatestRequest]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  return { neighborhoods, categories, loading, error, refetch: fetchData };
};
